import React, { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
// DHTMLX Gantt imports
import { gantt } from 'dhtmlx-gantt';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';

import { Spin, Alert, Card, Button, Typography, Radio, Space, Row, Col, Tooltip } from 'antd';
import { UnorderedListOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { usePatientPortal } from '../context/PatientPortalContext';
import OrderDetailsModal from './OrderDetailsModal';
import './PatientCalendar.css'; // Use same CSS as main patient chart

const { Title, Text, Paragraph } = Typography;

// Category configuration synchronized with main patient chart
const categoryConfig = {
  medications: { color: '#ff6b6b', label: 'Medications', ganttColor: '#ff6b6b' },
  nursing: { color: '#4ecdc4', label: 'Nursing', ganttColor: '#4ecdc4' },
  laboratory: { color: '#45b7d1', label: 'Laboratory', ganttColor: '#45b7d1' },
  radiology: { color: '#96ceb4', label: 'Radiology', ganttColor: '#96ceb4' },
  referral: { color: '#feca57', label: 'Referral', ganttColor: '#feca57' },
  lifestyle: { color: '#ff9ff3', label: 'Lifestyle', ganttColor: '#ff9ff3' },
  vaccine: { color: '#54a0ff', label: 'Vaccine', ganttColor: '#54a0ff' },
  preventive_screening: { color: '#90ee90', label: 'Preventive Screening', ganttColor: '#90ee90' },
  smart_goals: { color: '#f759ab', label: 'SMART Goals', ganttColor: '#f759ab' },
  appointments: { color: '#00d2d3', label: 'Appointments', ganttColor: '#00d2d3' },
  default: { color: '#dddddd', label: 'Other', ganttColor: '#dddddd' }
};

const categorySortOrder = [
  'medications', 'nursing', 'laboratory', 'radiology', 'referral',
  'lifestyle', 'vaccine', 'preventive_screening', 'smart_goals', 'appointments', 'default'
];

// Zoom configuration synchronized with main patient chart
const zoomConfig = {
  levels: [
    { name: "year", scale_height: 50, min_column_width: 50, scales: [{ unit: "year", step: 1, format: "%Y" }, { unit: "quarter", step: 1, format: (date) => `Q${Math.floor((date.getMonth() / 3)) + 1}` }] },
    { name: "quarter", scale_height: 50, min_column_width: 90, scales: [{ unit: "quarter", step: 1, format: (date) => `Q${Math.floor((date.getMonth() / 3)) + 1}, %Y` }, { unit: "month", step: 1, format: "%M" }] },
    { name: "month", scale_height: 50, min_column_width: 120, scales: [{ unit: "month", step: 1, format: "%F, %Y" }, { unit: "week", step: 1, format: (date) => `W${gantt.date.getWeek(date)}` }] },
    { name: "week", scale_height: 50, min_column_width: 80, scales: [{ unit: "week", step: 1, format: (date) => `Week ${gantt.date.getWeek(gantt.date.week_start(new Date(date)))} (${moment(gantt.date.week_start(new Date(date))).format("MMM D")})` }, { unit: "day", step: 1, format: "%d (%D)" }] }
  ],
  activeLevel: "week"
};

const PatientPortalTimeline = () => {
  const { patient, getPortalAxios } = usePatientPortal();
  const patientId = patient?._id;

  const [ordersAndAppointments, setOrdersAndAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTaskStatus, setSelectedTaskStatus] = useState('all');
  const [selectedTaskDetails, setSelectedTaskDetails] = useState(null);
  const [taskDetailsModalVisible, setTaskDetailsModalVisible] = useState(false);
  const ganttContainerRef = useRef(null);
  const isGanttInitialized = useRef(false);
  const [isReadyForGanttInit, setIsReadyForGanttInit] = useState(false);

  // Fetch events function adapted for portal authentication
  const fetchEvents = useCallback(async () => {
    if (!patientId) {
      console.log("PatientPortalTimeline: No patientId provided");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Use portal axios instance for authenticated requests
      const portalAxios = getPortalAxios();
      console.log("PatientPortalTimeline: Fetching timeline for patient:", patientId);
      const response = await portalAxios.get(`/portal/timeline`);
      const events = response.data?.data?.calendarEvents || []; // Updated to match main chart structure
      console.log("PatientPortalTimeline fetchEvents: Count:", events.length);
      setOrdersAndAppointments(events);
      setError(null);
    } catch (err) {
      console.error('PatientPortalTimeline fetchEvents: Error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load timeline events';
      setError(errorMessage);
      setOrdersAndAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, getPortalAxios]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // DHTMLX tasks state
  const [dhtmlxTasks, setDhtmlxTasks] = useState({ data: [], links: [] });

  // Set ready for Gantt initialization
  useEffect(() => {
    if (!loading && ganttContainerRef.current) {
      console.log("PatientPortalTimeline: Ready for Gantt Init signal.");
      setIsReadyForGanttInit(true);
    } else {
      setIsReadyForGanttInit(false);
    }
  }, [loading]);

  // Transform timeline data for DHTMLX Gantt (synchronized with main chart logic)
  useEffect(() => {
    if (loading) return;

    const filteredByStatus = ordersAndAppointments.filter(event => {
      if (selectedTaskStatus === 'all') return true;
      const eventData = event.originalEventDetails || event;
      return (eventData.status || 'Pending').toLowerCase() === selectedTaskStatus.toLowerCase();
    });

    let finalTasksData = [];

    if (selectedCategory === 'all') {
      const groupedByCategory = {};
      categorySortOrder.forEach(catKey => groupedByCategory[catKey] = []);

      console.log("PatientPortalTimeline: Initialized categories:", Object.keys(groupedByCategory));
      console.log("PatientPortalTimeline: Processing", filteredByStatus.length, "events");

      filteredByStatus.forEach(event => {
        const details = event.originalEventDetails || event;
        let originalCatKey = details.categoryName || 'default';
        let catKey = originalCatKey.toLowerCase().replace(/ /g, '_');

        // Handle category mapping for better grouping
        if (catKey === 'medication') catKey = 'medications';
        if (catKey === 'lab' || catKey === 'labs') catKey = 'laboratory';
        if (catKey === 'appointment') catKey = 'appointments';
        if (catKey === 'procedure') catKey = 'procedures';
        if (catKey === 'vaccines') catKey = 'vaccine';
        if (catKey === 'other_orders' || catKey === 'other') catKey = 'default';

        // If category doesn't exist in our config, try to map it or use default
        if (!groupedByCategory[catKey]) {
          // Try to find a matching category by checking if the key contains known category names
          let foundCategory = 'default';
          for (const configKey of categorySortOrder) {
            if (catKey.includes(configKey) || configKey.includes(catKey)) {
              foundCategory = configKey;
              break;
            }
          }
          console.log(`PatientPortalTimeline: Mapping category "${originalCatKey}" (${catKey}) to "${foundCategory}"`);
          catKey = foundCategory;
        }

        groupedByCategory[catKey].push(event);
      });

      console.log("PatientPortalTimeline: Final grouped categories:", Object.entries(groupedByCategory).map(([cat, events]) => `${cat}: ${events.length}`));

      categorySortOrder.forEach(categoryKey => {
        const categoryEvents = groupedByCategory[categoryKey];
        if (categoryEvents.length > 0) {
          categoryEvents.sort((a, b) => moment(a.start || a.createdAt).diff(moment(b.start || b.createdAt)));
          const firstEventDate = moment(categoryEvents[0].start || categoryEvents[0].createdAt);
          const lastEvent = categoryEvents[categoryEvents.length - 1];
          const lastEventEndDate = moment(lastEvent.end || moment(lastEvent.start || lastEvent.createdAt).add(1, 'hour'));

          finalTasksData.push({
            id: `project_${categoryKey}`, text: categoryConfig[categoryKey]?.label || 'Unknown', type: "project",
            start_date: firstEventDate.isValid() ? firstEventDate.format("DD-MM-YYYY HH:mm") : moment().format("DD-MM-YYYY HH:mm"),
            end_date: lastEventEndDate.isValid() ? lastEventEndDate.format("DD-MM-YYYY HH:mm") : moment().add(1,'day').format("DD-MM-YYYY HH:mm"),
            open: true, color: categoryConfig[categoryKey]?.ganttColor, task_class: `gantt-project-category-${categoryKey}`
          });

          categoryEvents.forEach(event => {
            const originalEventDetails = event.originalEventDetails || event; const topLevelEvent = event;
            const startDateMoment = moment(topLevelEvent.start || topLevelEvent.createdAt);
            const validStartDate = startDateMoment.isValid() ? startDateMoment.toDate() : new Date();
            const taskCatKey = (originalEventDetails.categoryName || 'default').toLowerCase().replace(/ /g, '_');
            let taskType = taskCatKey === 'preventive_screening' ? "milestone" : "task";
            let taskColor = categoryConfig[taskCatKey]?.ganttColor || categoryConfig.default.ganttColor;
            let dhtmlxEndDateStr;
            if (taskCatKey === 'preventive_screening') dhtmlxEndDateStr = moment(validStartDate).format("DD-MM-YYYY HH:mm");
            else {
              let endDateMoment = moment(topLevelEvent.end || moment(validStartDate).add(1, 'hour'));
              if (!endDateMoment.isValid() || endDateMoment.isBefore(startDateMoment)) endDateMoment = moment(validStartDate).add(1, 'hour');
              dhtmlxEndDateStr = endDateMoment.format("DD-MM-YYYY HH:mm");
            }
            let taskText = topLevelEvent.name;
            const orderType = originalEventDetails.orderType; const appointmentType = originalEventDetails.appointmentType;
            if (!taskText || (orderType && taskText.toLowerCase()===orderType.toLowerCase()) || (appointmentType && taskText.toLowerCase()===appointmentType.toLowerCase())) {
              if (topLevelEvent.originalEventType==='order' && orderType==='Laboratory') taskText = `Lab: ${originalEventDetails.testName||originalEventDetails.description||'N/A'}`;
              else if (topLevelEvent.originalEventType==='order' && orderType==='Medication') taskText = originalEventDetails.medicationName || originalEventDetails.description || 'Medication';
              else if (topLevelEvent.originalEventType==='order' && orderType==='Radiology') taskText = `Imaging: ${originalEventDetails.studyDescription||originalEventDetails.description||'N/A'}`;
              else if (topLevelEvent.originalEventType==='order' && orderType==='Referral') taskText = `Referral: ${originalEventDetails.referredToProvider||originalEventDetails.specialty||'N/A'}`;
              else if (topLevelEvent.originalEventType==='order') taskText = String(originalEventDetails.orderDetails?.description||orderType||'Order');
              else if (topLevelEvent.originalEventType==='appointment') taskText = String(appointmentType||originalEventDetails.reason||'Appointment');
              else taskText = String(originalEventDetails.description||'Event');
            }
            if (!taskText || taskText.trim()==='') taskText = 'Unnamed Event';
            const currentStatus = originalEventDetails.status||'Pending';
            finalTasksData.push({
              id: String(topLevelEvent.id || `event-${Math.random().toString(36).substr(2,9)}`), text: taskText, parent: `project_${categoryKey}`,
              start_date: moment(validStartDate).format("DD-MM-YYYY HH:mm"), end_date: dhtmlxEndDateStr, open: true, type: taskType, color: taskColor,
              task_class: `gantt-task-category-${taskCatKey}`, notes: originalEventDetails.notes||'', status_custom: currentStatus,
              _originalEventData: { ...topLevelEvent, originalEventDetails }
            });
          });
        }
      });
    } else {
      finalTasksData = filteredByStatus
        .filter(event => (event.originalEventDetails||event).categoryName?.toLowerCase().replace(/ /g,'_') === selectedCategory)
        .map(event => {
            const originalEventDetails = event.originalEventDetails||event; const topLevelEvent = event;
            const startDateMoment = moment(topLevelEvent.start||topLevelEvent.createdAt);
            const validStartDate = startDateMoment.isValid()?startDateMoment.toDate():new Date();
            const taskCatKey = (originalEventDetails.categoryName||'default').toLowerCase().replace(/ /g,'_');
            let taskType = taskCatKey === 'preventive_screening' ? "milestone" : "task";
            let taskColor = categoryConfig[taskCatKey]?.ganttColor || categoryConfig.default.ganttColor;
            let dhtmlxEndDateStr;
            if (taskCatKey === 'preventive_screening') dhtmlxEndDateStr = moment(validStartDate).format("DD-MM-YYYY HH:mm");
            else {
              let endDateMoment = moment(topLevelEvent.end||moment(validStartDate).add(1,'hour'));
              if(!endDateMoment.isValid()||endDateMoment.isBefore(startDateMoment)) endDateMoment = moment(validStartDate).add(1,'hour');
              dhtmlxEndDateStr = endDateMoment.format("DD-MM-YYYY HH:mm");
            }
            let taskText = topLevelEvent.name;
            const orderType = originalEventDetails.orderType; const appointmentType = originalEventDetails.appointmentType;
            if(!taskText||(orderType&&taskText.toLowerCase()===orderType.toLowerCase())||(appointmentType&&taskText.toLowerCase()===appointmentType.toLowerCase())){
              if(topLevelEvent.originalEventType==='order'&&orderType==='Laboratory'){
                taskText=`Lab: ${originalEventDetails.testName||originalEventDetails.description||'N/A'}`;
              } else if(topLevelEvent.originalEventType==='order'&&orderType==='Lifestyle'){
                // Check if this is a new card-based lifestyle order
                if(originalEventDetails.cardId && originalEventDetails.title) {
                  // New card-based lifestyle order
                  taskText = originalEventDetails.title;
                  // Add first SMART goal as subtitle if available
                  if(originalEventDetails.smartGoals && originalEventDetails.smartGoals.length > 0) {
                    taskText += ` - ${originalEventDetails.smartGoals[0]}`;
                  }
                } else {
                  // Legacy lifestyle order format
                  if(originalEventDetails.lifestyleCategory === 'Nutrition'){
                    taskText = originalEventDetails.dietaryRecommendations ||
                             originalEventDetails.calorieTarget ||
                             originalEventDetails.specificFoodsToInclude ||
                             originalEventDetails.lifestyleGeneralInstructions ||
                             'Nutrition guidance';
                  } else if(originalEventDetails.lifestyleCategory === 'Physical Activity'){
                    taskText = originalEventDetails.specificExercises ||
                             `${originalEventDetails.activityType || 'Exercise'} - ${originalEventDetails.frequencyPA || ''} ${originalEventDetails.durationPA || ''}`.trim() ||
                             originalEventDetails.lifestyleGeneralInstructions ||
                             'Physical activity plan';
                  } else if(originalEventDetails.lifestyleCategory === 'Rest'){
                    taskText = originalEventDetails.sleepHygieneRecommendations ||
                             originalEventDetails.stressReductionTechniques ||
                             `Sleep target: ${originalEventDetails.sleepDurationTarget || ''}`.trim() ||
                             originalEventDetails.lifestyleGeneralInstructions ||
                             'Rest and recovery plan';
                  } else if(originalEventDetails.lifestyleCategory === 'Psychosocial Management'){
                    taskText = originalEventDetails.psychosocialGoals ||
                             originalEventDetails.psychosocialReferralDetails ||
                             originalEventDetails.lifestyleGeneralInstructions ||
                             'Psychosocial support plan';
                  } else {
                    taskText = originalEventDetails.lifestyleGeneralInstructions ||
                             originalEventDetails.description ||
                             'Lifestyle guidance';
                  }
                }
              } else if(topLevelEvent.originalEventType==='order'&&orderType==='Radiology'){
                taskText=`Imaging: ${originalEventDetails.studyDescription||originalEventDetails.description||'N/A'}`;
              } else if(topLevelEvent.originalEventType==='order'&&orderType==='Medication'){
                taskText=`Med: ${originalEventDetails.medicationName||originalEventDetails.description||'N/A'}`;
              } else if(topLevelEvent.originalEventType==='order'&&orderType==='Referral'){
                taskText=`Referral: ${originalEventDetails.referredToProvider||originalEventDetails.specialty||'N/A'}`;
              } else if(topLevelEvent.originalEventType==='order'){
                taskText=String(originalEventDetails.description||orderType||'Order');
              } else if(topLevelEvent.originalEventType==='appointment'){
                taskText=String(appointmentType||originalEventDetails.reason||'Appointment');
              } else {
                taskText=String(originalEventDetails.description||'Event');
              }
            }
            if(!taskText||taskText.trim()==='')taskText='Unnamed Event';
            const currentStatus=originalEventDetails.status||'Pending';
            return {
              id:String(topLevelEvent.id||`event-${Math.random().toString(36).substr(2,9)}`),text:taskText,start_date:moment(validStartDate).format("DD-MM-YYYY HH:mm"),
              end_date:dhtmlxEndDateStr,open:true,type:taskType,color:taskColor,
              task_class:`gantt-task-category-${taskCatKey}`,notes:originalEventDetails.notes||'',status_custom:currentStatus,
              _originalEventData:{...topLevelEvent,originalEventDetails}
            };
        });
    }

    console.log("PatientPortalTimeline transformEffect: finalTasksData count:", finalTasksData.length);

    // Debug: Log project headers being created
    const projectHeaders = finalTasksData.filter(task => task.type === "project");
    console.log("PatientPortalTimeline Project headers created:", projectHeaders.length, projectHeaders.map(h => h.text));
    console.log("PatientPortalTimeline All tasks data:", finalTasksData.map(t => ({ id: t.id, text: t.text, type: t.type, parent: t.parent })));

    setDhtmlxTasks({ data: finalTasksData, links: [] });
  }, [ordersAndAppointments, selectedCategory, selectedTaskStatus, loading]);

  // Initialize DHTMLX Gantt
  useEffect(() => {
    if (!isReadyForGanttInit || isGanttInitialized.current || !ganttContainerRef.current) return;

    console.log("PatientPortalTimeline: Initializing Gantt instance...");

    // Ensure complete cleanup before initialization
    if (ganttContainerRef.current.innerHTML !== "") {
      ganttContainerRef.current.innerHTML = "";
    }

    // Reset gantt to default state before configuration
    try {
      if (gantt && typeof gantt.resetLayout === 'function') {
        gantt.resetLayout();
      }

      // Clear any existing data stores
      if (gantt && typeof gantt.clearAll === 'function') {
        gantt.clearAll();
      }
    } catch (error) {
      console.warn("PatientPortalTimeline: Error during gantt reset:", error);
    }

    // Configure gantt settings
    gantt.config.date_format = "%d-%m-%Y %H:%i";
    gantt.config.xml_date = "%d-%m-%Y %H:%i";
    gantt.config.readonly = true;
    gantt.config.autosize = "y";
    gantt.config.sort = false;
    gantt.config.open_tree_initially = true;
    gantt.config.auto_types = true;
    gantt.config.show_progress = false;
    gantt.config.show_links = false;

    // Define task types explicitly
    gantt.config.types = {
      task: "task",
      project: "project",
      milestone: "milestone"
    };

    // Ensure project tasks are properly rendered
    gantt.config.project_start = "start_date";
    gantt.config.project_end = "end_date";

    gantt.config.columns = [
      { name: "text", label: "Task/Event Name", tree: true, width: '*', resize: false, template: (task) => task.text }
    ];

    gantt.config.lightbox.sections = [
      { name: "description", height: 70, map_to: "text", type: "template", label: "Name/Description" },
      { name: "status_custom_display", height: 30, map_to: "status_custom", type: "template", label: "Status" },
      { name: "notes_display", height: 70, map_to: "notes", type: "template", label: "Notes" },
      { name: "time", type: "time", map_to: "auto", readonly:true, label: "Time Period" }
    ];

    gantt.templates.task_class = (start, end, task) => task.task_class || (task.type === "project" ? "gantt_project_task" : "");
    gantt.templates.tooltip_text = (start, end, task) => {
      return `<b>${task.text}</b><br/>Status: ${task.status_custom || 'N/A'}<br/>Notes: ${task.notes || 'None'}`;
    };
    gantt.templates.task_text = (start, end, task) => task.text;

    // Initialize plugins before gantt.init()
    try {
      gantt.plugins({ context_menu: true, zoom: true });
    } catch (error) {
      console.warn("PatientPortalTimeline: Error initializing plugins:", error);
    }

    // Initialize gantt with error handling
    try {
      gantt.init(ganttContainerRef.current);
      console.log("PatientPortalTimeline: gantt.init() called successfully.");
      isGanttInitialized.current = true;
    } catch (error) {
      console.error("PatientPortalTimeline: Error during gantt.init():", error);
      isGanttInitialized.current = false;
      return; // Exit early if initialization failed
    }

    // Initialize zoom extension with error handling
    try {
      if (gantt.ext && gantt.ext.zoom) {
        gantt.ext.zoom.init(zoomConfig);
        gantt.ext.zoom.setLevel(zoomConfig.activeLevel || "week"); // Match main chart default
      } else {
        console.warn("DHTMLX Zoom extension not available.");
      }
    } catch (error) {
      console.warn("PatientPortalTimeline: Error initializing zoom:", error);
    }

    // Event handlers
    gantt.attachEvent("onTaskClick", function(id, e) {
      const task = gantt.getTask(id);
      if (task._originalEventData) {
        setSelectedTaskDetails(task._originalEventData);
        setTaskDetailsModalVisible(true);
      }
      return false; // Prevent default lightbox
    });

    return () => {
      try {
        if (isGanttInitialized.current) {
          gantt.clearAll();
          isGanttInitialized.current = false;
        }
      } catch (error) {
        console.warn("PatientPortalTimeline: Error during cleanup:", error);
      }
    };
  }, [isReadyForGanttInit]);

  // Parse data into Gantt
  useEffect(() => {
    if (isGanttInitialized.current && isReadyForGanttInit && !loading && ganttContainerRef.current) {
      console.log("PatientPortalTimeline: Parsing data. Tasks count:", dhtmlxTasks.data.length);
      try {
        gantt.clearAll();
        gantt.parse(dhtmlxTasks);
      } catch (error) {
        console.error("PatientPortalTimeline: Error parsing gantt data:", error);
        // Try to recover by reinitializing
        isGanttInitialized.current = false;
        setIsReadyForGanttInit(false);
        setTimeout(() => {
          setIsReadyForGanttInit(true);
        }, 100);
      }
    }
  }, [dhtmlxTasks, isReadyForGanttInit, loading]);

  // Control handlers
  const handleZoomIn = () => {
    if (isGanttInitialized.current && gantt.ext?.zoom) gantt.ext.zoom.zoomOut();
  };

  const handleZoomOut = () => {
    if (isGanttInitialized.current && gantt.ext?.zoom) gantt.ext.zoom.zoomIn();
  };

  const handleTaskDetailsModalClose = () => {
    setSelectedTaskDetails(null);
    setTaskDetailsModalVisible(false);
  };

  // Loading and error states
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading your health timeline...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Timeline"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={fetchEvents}>
            Retry
          </Button>
        }
      />
    );
  }

  // Enhanced order details modal
  const renderTaskDetailsModal = () => (
    <OrderDetailsModal
      visible={taskDetailsModalVisible}
      onClose={handleTaskDetailsModalClose}
      orderData={selectedTaskDetails}
      categoryConfig={categoryConfig}
    />
  );

  return (
    <div className="patient-calendar"> {/* Use same CSS class as main chart */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <Title level={4} style={{ margin: 0 }}>
          Patient Care Timeline (DHTMLX - Read-Only)
        </Title>
        <Space align="center">
          <Tooltip title="Zoom In">
            <Button icon={<ZoomInOutlined />} onClick={handleZoomIn} size="small" />
          </Tooltip>
          <Tooltip title="Zoom Out">
            <Button icon={<ZoomOutOutlined />} onClick={handleZoomOut} size="small" />
          </Tooltip>
        </Space>
      </div>

      {/* Filter Controls - synchronized with main chart */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Text strong>Status Filter: </Text>
            <Radio.Group
              value={selectedTaskStatus}
              onChange={(e) => setSelectedTaskStatus(e.target.value)}
              style={{ marginLeft: '8px' }}
              size="small"
            >
              <Radio.Button value="all">All Statuses</Radio.Button>
              <Radio.Button value="Pending">Pending</Radio.Button>
              <Radio.Button value="Active">Active</Radio.Button>
              <Radio.Button value="Completed">Completed</Radio.Button>
              <Radio.Button value="Cancelled">Cancelled</Radio.Button>
              <Radio.Button value="Resulted">Resulted</Radio.Button>
            </Radio.Group>
          </Col>
          <Col xs={24} md={12}>
            <Text strong>Category Filter: </Text>
            <Radio.Group
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{ marginLeft: '8px' }}
              size="small"
            >
              <Radio.Button value="all">All Categories</Radio.Button>
              {Object.entries(categoryConfig).map(([key, config]) => (
                key !== 'default' && (
                  <Radio.Button key={key} value={key}>
                    <span style={{ color: config.color, marginRight: '5px' }}>●</span>
                    {config.label}
                  </Radio.Button>
                )
              ))}
            </Radio.Group>
          </Col>
        </Row>
      </Card>

      {/* Interactive Gantt Timeline */}
      <Card style={{ padding: '24px' }}>
        <div
          ref={ganttContainerRef}
          style={{ width: '100%', height: '550px' }}
        />
        {dhtmlxTasks.data.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
            <Text>No scheduled events to display for the selected criteria.</Text>
          </div>
        )}
      </Card>

      {/* Information Card */}
      <Card style={{ marginTop: '16px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <UnorderedListOutlined style={{ color: '#52c41a' }} />
          <div>
            <Text strong style={{ color: '#389e0d' }}>Interactive Timeline</Text>
            <Paragraph style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#389e0d' }}>
              This interactive timeline shows your complete health journey. Use the zoom controls to adjust the view,
              filter by category or status, and click on any event for detailed information.
            </Paragraph>
          </div>
        </div>
      </Card>

      {/* Task Details Modal */}
      {renderTaskDetailsModal()}
    </div>
  );
};

export default PatientPortalTimeline;
